import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import {
  Box,
  CssBaseline,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ThemeProvider
} from '@mui/material';
import Button from '@mui/material/Button';
import { isEmpty } from 'lodash';
import { DataGrid } from '@mui/x-data-grid';
import themev5 from '../../themev5';
import Actions from '../../store/actions';
import { getAllDataQuality } from '../../api/china';
import Styles from '../Canada/styles/curationdashboard.styles';
import Store from '../../store';
import styles from '../Datagrid/Datagrid.styles';
import DatagridFooter from '../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../Datagrid/DatagridNoRowsOverlay';

const ListTranslationQuality = () => {
  const { dispatch } = useContext<any>(Store);
  const classes = Styles();

  const history = useHistory();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageData, setPageData] = useState({
    pageNo: 0,
    pageSize: 10
  });
  const [primaryNumber, setPrimaryNumber] = useState('');
  const [qcStatus, setQcStatus] = useState('All');
  const [priority, setPriority] = useState('All');
  const [totalRowCount, setTotalRowCount] = useState(0);
  const rowsPerPageOptions = [10, 20, 50, 100];

  const searchFields = [
    {
      id: 'vin',
      label: 'VIN Number',
      value: primaryNumber,
      onChange: (value: string) => {
        setPrimaryNumber(value);
      }
    },
    {
      id: 'highPriority',
      label: 'High Priority',
      options: ['Yes'],
      value: priority,
      onChange: (value: string) => {
        setPriority(value);
      }
    },
    {
      id: 'qc_status',
      label: 'Qc status',
      options: [
        'All Qced',
        'Drug Qced',
        'Extracted Qced',
        'All pending',
        'Drug pending',
        'Extracted pending'
      ],
      value: qcStatus,
      onChange: (value: string) => {
        setQcStatus(value);
      }
    }
  ];
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getAllDataQuality(
        pageData.pageSize,
        pageData.pageNo,
        qcStatus,
        primaryNumber,
        priority
      );
      if (response === undefined) {
        throw new Error('Result is undefined');
      }
      if (response.data && response.data.Success.data) {
        setIsLoading(false);
        const resultData = response.data.Success.data;
        setData(resultData);
        setTotalRowCount(response.data.Success.count);
        dispatch({
          type: Actions.SET_CHINA_DATA_QUALITY_VIN_LIST,
          value: response.data.Success.vinList
        });
        //    save to local storage
        localStorage.setItem(
          'chinaDataQualityVinList',
          JSON.stringify(response.data.Success.vinList)
        );
      } else {
        setIsLoading(false);
        throw Error('No data');
      }
    } catch (e: any) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: e.message }
      });
    } finally {
      setIsLoading(false);
    }
  }, [pageData, primaryNumber, qcStatus, priority]);

  const DATAGRID_COLUMNS: any = [
    {
      field: 'vin',
      headerName: 'Vin Number',
      width: 120,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'generic_name',
      headerName: 'Generic Name / Active Ingredient',
      width: 350,
      headerAlign: 'center',
      align: 'left'
    },
    {
      field: 'trade_name',
      headerName: 'Drug Name / Trade Name',
      width: 350,
      headerAlign: 'center',
      align: 'left'
    },
    {
      field: 'qc_status',
      headerName: 'QC Status',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: any) => {
        return (
          <Typography
            variant='body1'
            sx={{
              color: params.row.qc_status === 'High Priority' ? 'red' : 'green',
              fontWeight: 'bold'
            }}>
            Drug : {params.row.qc_status?.drug?.status || 'Not done'} <br />
            Extracted : {params.row.qc_status?.extracted?.status || 'Not done'} <br />
          </Typography>
        );
      }
    },
    {
      field: 'Edit',
      width: 500,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: any) => {
        return (
          <Stack direction='row' spacing={2}>
            <Box>
              <Button
                variant={isEmpty(params.row.qc_status) ? 'outlined' : 'contained'}
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  history.push(`/china/quality/translations/${params.row.vin}`);
                }}>
                <Typography>Fix data</Typography>
              </Button>
            </Box>
          </Stack>
        );
      }
    }
  ];

  useEffect(() => {
    fetchData();
  }, [pageData]);

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Box display='flex' ml={2} mt={10} alignItems='center'>
        {searchFields.map(row => {
          if ('options' in row) {
            return (
              <FormControl>
                <InputLabel id={`${row.id}-label`}>{row.label}</InputLabel>
                <Select
                  labelId={`${row.id}-label`}
                  id={row.id}
                  value={row.value}
                  label={row.label}
                  sx={{ p: '0', minWidth: '250px', mr: '20px' }}
                  onChange={(event: SelectChangeEvent) => {
                    row.onChange(event.target.value as string);
                  }}>
                  <MenuItem value='All'>All</MenuItem>
                  {row?.options?.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }
          return (
            <TextField
              key={row.id}
              variant='outlined'
              type='search'
              id={row.id}
              label={row.label}
              value={row.value}
              sx={{ paddingRight: '30px' }}
              InputProps={{
                style: { fontSize: '1.5vh', minWidth: '250px', paddingRight: '20px' }
              }}
              InputLabelProps={{
                style: { fontSize: '1.5vh' }
              }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                row.onChange(event.target.value);
              }}
            />
          );
        })}
        <Button
          onClick={() => {
            setPageData({ pageNo: 0, pageSize: pageData.pageSize });
          }}
          variant='contained'
          sx={{ textTransform: 'none' }}>
          <Typography>Search</Typography>
        </Button>
      </Box>
      <Box
        height='80vh'
        p={2}
        mt={1}
        display='flex'
        alignItems='center'
        justifyContent='center'
        className={classes.root}>
        <DataGrid
          sx={styles.datagrid}
          style={{ height: '100%' }}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={data}
          rowCount={totalRowCount}
          columns={DATAGRID_COLUMNS}
          loading={isLoading}
          getRowId={(row: any) => row.id}
          paginationMode='server'
          getRowClassName={() => `table-rows`}
          pageSize={pageData.pageSize}
          page={pageData.pageNo}
          onPageSizeChange={newPageSize => {
            setPageData({ pageNo: 0, pageSize: newPageSize });
          }}
          onPageChange={number => {
            setPageData(prevState => ({ ...prevState, pageNo: number }));
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
          pagination
        />
      </Box>
    </ThemeProvider>
  );
};
export default ListTranslationQuality;
